import { useEffect, useMemo, useRef, useState } from 'react'
import { useUpdateEffect } from 'react-use'
import styled, { keyframes } from 'styled-components'

import { Duration } from '@blade/std-lib'

import { directionOf, type FlashDirection } from './read-number'

import type { ChildrenProps, ClassNameProps } from '@root/shared/react'

export const DEFAULT_FLASH_DURATION = Duration.millis(800)

export type { FlashDirection }

const useFlashRun = (pulse: number, milliseconds: number) => {
  const [runKey, setRunKey] = useState<number | undefined>(undefined)

  useUpdateEffect(() => {
    if (pulse === 0) {
      return
    }

    setRunKey(pulse)

    const id = setTimeout(() => setRunKey(undefined), milliseconds)
    return () => clearTimeout(id)
  }, [pulse, milliseconds])

  return runKey
}

const flashStyle = (milliseconds: number): React.CSSProperties =>
  ({ '--milliseconds': `${milliseconds}ms` }) as React.CSSProperties

type FlashProps = {
  pulse: number
  duration: Duration.Duration
  /** Colours the flash; without it the flash is the neutral amber. */
  direction?: FlashDirection
} & ChildrenProps &
  ClassNameProps

export const Flash = ({
  pulse,
  duration,
  direction,
  children,
  className,
}: FlashProps) => {
  const milliseconds = Duration.toMillis(duration)
  const runKey = useFlashRun(pulse, milliseconds)
  const style = useMemo(() => flashStyle(milliseconds), [milliseconds])

  return (
    <Root className={className} style={style}>
      {runKey !== undefined && (
        <FlashOverlay key={runKey} style={style} data-direction={direction} />
      )}
      {children}
    </Root>
  )
}

export type FlashTextContentProps = {
  duration?: Duration.Duration
  disabled?: boolean
} & ChildrenProps &
  ClassNameProps

export const FlashTextContent = ({
  duration = DEFAULT_FLASH_DURATION,
  disabled = false,
  children,
  className,
}: FlashTextContentProps) => {
  const rootRef = useRef<HTMLDivElement>(null)
  // Pulse and direction move together: the overlay is keyed on the pulse, so
  // a new one must mount already knowing which way the figure went.
  const [run, setRun] = useState<{ pulse: number; direction: FlashDirection }>({
    pulse: 0,
    direction: undefined,
  })

  useEffect(() => {
    if (disabled) {
      return
    }

    const root = rootRef.current
    if (root === null) {
      return
    }

    let prevText = root.textContent

    const observer = new MutationObserver(() => {
      const nextText = root.textContent
      if (nextText === prevText) {
        return
      }
      const direction = directionOf(prevText, nextText)
      prevText = nextText
      setRun((current) => ({ pulse: current.pulse + 1, direction }))
    })

    observer.observe(root, {
      childList: true,
      characterData: true,
      subtree: true,
    })

    return () => observer.disconnect()
  }, [disabled])

  const milliseconds = Duration.toMillis(duration)
  const runKey = useFlashRun(run.pulse, milliseconds)
  const style = useMemo(() => flashStyle(milliseconds), [milliseconds])

  return (
    <Root ref={rootRef} className={className} style={style}>
      {!disabled && runKey !== undefined && (
        <FlashOverlay
          key={runKey}
          style={style}
          data-direction={run.direction}
        />
      )}
      {children}
    </Root>
  )
}

const flashFade = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`

const Root = styled.div`
  position: relative;
  width: fit-content;
  border-radius: 2px;
`

/**
 * Amber says "this moved"; green and red say which way.
 *
 * A trader reads direction faster than they read the number, and every figure
 * flashing the same colour carries none. The colour is a static background and
 * only the opacity animates, so a `var()` never has to resolve inside a
 * keyframe.
 */
const FlashOverlay = styled.div`
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  background-color: var(--amberA-6);
  animation: ${flashFade} var(--milliseconds) cubic-bezier(0.4, 0, 0.2, 1);

  &[data-direction='up'] {
    background-color: var(--upA-6);
  }

  &[data-direction='down'] {
    background-color: var(--downA-6);
  }
`
